import { useEffect, useState } from "react";
import operatorApiService from "../../../../services/operator-api.service";
import editIcon from "../../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../../assets/imgs/master/delete_icon_wh.svg";

interface MonitorConsoleProps {
  baseId: number | null;
}

interface EditableMonitorConsole {
  monitor_console_id: number | null;
  monitor_console_no: string;
  base_id: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
  original_id?: number;
}

const MonitorConsole = ({ baseId }: MonitorConsoleProps) => {
  const [originalRows, setOriginalRows] = useState<EditableMonitorConsole[]>(
    []
  );
  const [rows, setRows] = useState<EditableMonitorConsole[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!baseId) return;

    const fetchMonitorConsoles = async () => {
      const res = await operatorApiService.doGetMonitorConsoles({ baseId });
      const initial = res.map((entry: any) => ({
        ...entry,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.monitor_console_id}`,
      }));
      setOriginalRows(initial);
      setRows(initial);
    };

    fetchMonitorConsoles();
  }, [baseId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        monitor_console_id: null,
        monitor_console_no: "",
        base_id: baseId!,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}-${Math.random()}`,
      },
    ]);
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? {
              ...r,
              isEditing: true,
              status: r.status === "original" ? "edited" : r.status,
              original_id: r.monitor_console_id ?? undefined,
            }
          : r
      )
    );
  };

  const handleChange = (
    index: number,
    field: keyof EditableMonitorConsole,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.monitor_console_no.trim() === ""
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.monitor_console_no.trim().length > 6
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(({ monitor_console_no }) => ({
          monitor_console_no,
          base_id: baseId!,
        }));

      const update = rows
        .filter((r) => r.status === "edited")
        .map(({ monitor_console_id, monitor_console_no }) => ({
          monitor_console_id,
          monitor_console_no,
        }));

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.monitor_console_id);

      if (create.length === 0 && update.length === 0 && del.length === 0) {
        return;
      }

      const response = await operatorApiService.saveMonitorConsoles({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetMonitorConsoles({
          baseId,
        });
        const mapped = refreshed.map((entry: any) => ({
          ...entry,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.monitor_console_id}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleResetAll = () => {
    const reset = originalRows.map((r) => ({ ...r }));
    setRows(reset);
  };

  return (
    <div className="section">
      <div className="sectionTitle">監視卓</div>
      <div className="sectionBody">
        <table className="tableArea">
          <thead>
            <tr>
              <th>監視卓ID</th>
              <th>監視卓番号</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.internalId}>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.monitor_console_id ?? "新規"}
                </td>
                <td className={row.status === "deleted" ? "deleteCell" : ""}>
                  {row.isEditing ? (
                    <input
                      value={row.monitor_console_no}
                      onChange={(e) =>
                        handleChange(i, "monitor_console_no", e.target.value)
                      }
                    />
                  ) : (
                    row.monitor_console_no
                  )}
                </td>
                <td>
                  {row.status === "deleted" ? (
                    <span className="deletionLabel">削除予定</span>
                  ) : row.status === "edited" ? (
                    <span className="editLabel">編集予定</span>
                  ) : row.status === "new" ? (
                    <button
                      className="cancelButton"
                      onClick={() => markDeleted(row.internalId)}
                    >
                      取消
                    </button>
                  ) : (
                    <div className="buttonGroup">
                      <button
                        className="updateButton"
                        onClick={() => toggleEdit(row.internalId)}
                      >
                        <img src={editIcon} alt="" className="icon" />
                        編集
                      </button>
                      <button
                        className="deleteButton"
                        onClick={() => markDeleted(row.internalId)}
                      >
                        <img src={deleteIcon} alt="" className="icon" />
                        削除
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="createFooter">
          <div className="createLabel">
            表に新規入力欄が追加されます。新しい監視卓を入力し、登録してください。
          </div>
          <button className="createButton" onClick={addRow}>
            新規行追加
          </button>
        </div>
        {successMessage && (
          <div className="successMessage">{successMessage}</div>
        )}
        {errorMessage && <div className="errorMessage">{errorMessage}</div>}
        {rows.some((r) => r.status !== "original") && (
          <div className="actionButtons">
            <button className="backButton" onClick={handleResetAll}>
              戻る
            </button>
            <button className="decisionButton" onClick={submit}>
              確定
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorConsole;
